import React, { useEffect, useState } from "react";
import GoalModal from "../GoalModal/GoalModal";
import './style.css';
import { CheckIcon } from "../../assets/icons/icons";
import { supabase } from '../../supabaseClient'; // Import supabase client

const DEFAULT_TARGETS = {
  Once: 1,
  Daily: 7,
  Weekly: 4,
  Monthly: 30,
  Forever: 1,
};

const getWindowStartDate = (frequency) => {
  const now = new Date();
  const clone = new Date(now);
  if (frequency === 'Monthly') {
    clone.setDate(clone.getDate() - 30);
    return clone.toISOString();
  }
  if (frequency === 'Weekly' || frequency === 'Daily') {
    clone.setDate(clone.getDate() - 7);
    return clone.toISOString();
  }
  if (frequency === 'Once' || frequency === 'Forever') {
    clone.setDate(clone.getDate() - 3650);
    return clone.toISOString();
  }
  clone.setDate(clone.getDate() - 7);
  return clone.toISOString();
};

const getLabelSuffix = (frequency) => {
  if (frequency === 'Monthly') return ' this month';
  if (frequency === 'Weekly') return ' this week';
  if (frequency === 'Daily') return ' this week';
  return '';
};

const GoalTracker = () => {
  const [goals, setGoals] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null); // For editing a goal
  const [progress, setProgress] = useState({}); // { [goalId]: { count, target, percent, label } }

  const getTargetForGoal = (goal) => {
    if (!goal) return 1;
    const raw = goal.targetcount ?? goal.targetCount;
    const parsed = parseInt(raw, 10);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
    return DEFAULT_TARGETS[goal.frequency] || 1;
  };

  const normalizeTargetCount = (value, frequency) => {
    const parsed = parseInt(value, 10);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
    return DEFAULT_TARGETS[frequency] || 1;
  };

  // Fetch active goals from Supabase when component loads
  useEffect(() => {
    const fetchGoals = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const userId = session.user.id;
      const { data, error } = await supabase
        .from('goals')
        .select('*')
        .eq('userid', userId)
        .eq('status', true); // Only fetch active goals

      if (error) {
      } else {
        setGoals(data); // Set only active goals
      }
    };

    fetchGoals();
  }, []);

  // Compute and fetch progress for visible goals
  useEffect(() => {
    const loadProgress = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const uid = session?.user?.id;
      if (!uid || goals.length === 0) return;

      const updates = {};

      for (const g of goals) {
        const target = getTargetForGoal(g);
        const from = getWindowStartDate(g.frequency);
        try {
          let query = supabase
            .from('goal_progress')
            .select('id, goalid, createdat, amount')
            .eq('userid', uid)
            .eq('goalid', g.id);
          if (from) {
            query = query.gte('createdat', from);
          }
          const { data, error } = await query;

          let count = 0;
          if (!error && Array.isArray(data)) {
            count = data.reduce((sum, row) => sum + (row?.amount ?? 1), 0);
          } else {
            // Fallback to local cache if table missing
            const key = `goal_progress_cache_${uid}`;
            const cache = JSON.parse(localStorage.getItem(key) || '[]');
            count = cache
              .filter(r => r.goalid === g.id && (!from || new Date(r.createdat) >= new Date(from)))
              .reduce((sum, row) => sum + (row?.amount ?? 1), 0);
          }

          const percent = Math.max(0, Math.min(100, Math.round((count/target)*100)));
          const suffix = getLabelSuffix(g.frequency);
          const label = `${count}/${target}${suffix ? suffix : ''}`;
          updates[g.id] = { count, target, percent, label };
        } catch (e) {
          // ignore
        }
      }

      setProgress(updates);
    };

    loadProgress();
  }, [goals]);

  // Add goal to the state and to Supabase
  const addGoal = async (goal) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
  
    const userId = session.user.id;
    const payload = {
      ...goal,
      targetcount: normalizeTargetCount(goal.targetcount, goal.frequency),
      userid: userId,
      createdat: new Date(),
      status: true,
    };
  
    const { data, error } = await supabase
      .from('goals')
      .insert([payload])
      .select(); // Ensure you're selecting the inserted data
  
    if (error) {
    } else if (data && data.length > 0) { // Ensure data exists and has at least one element
      setGoals([...goals, data[0]]); // Add new goal to the list
      // window.location.href = '/';
    }
  };
  

  // Open the modal for editing a goal
  const editGoal = (goal) => {
    setSelectedGoal(goal);
    setIsModalOpen(true);
  };

  // Update a goal in Supabase
  const updateGoal = async (goal) => {
    const payload = {
      ...goal,
      targetcount: normalizeTargetCount(goal.targetcount ?? goal.targetCount, goal.frequency),
    };
    const { data, error } = await supabase
      .from('goals')
      .update(payload)
      .eq('id', payload.id)
      .select(); // Get the updated goal

    if (error) {
    } else if (data && data.length > 0) {
      const updatedGoals = goals.map(g => g.id === goal.id ? data[0] : g);
      setGoals(updatedGoals);
      window.location.href='/'
    }
  };

  // Mark goal as completed
  const markGoalAsCompleted = async (goalId) => {
    const completedAt = new Date(); // Get current date and time
    const { data, error } = await supabase
      .from('goals')
      .update({ status: false, completedat: completedAt }) // Set status to false to mark it as completed
      .eq('id', goalId)
      .select(); // Ensure we get the updated goal

    if (error) {
    } else if (data && data.length > 0) {
      const updatedGoals = goals.map(g => g.id === goalId ? data[0] : g);
      setGoals(updatedGoals);
      window.location.href='/'
    }
  }; 

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    setSelectedGoal(null); // Clear the selected goal when adding a new goal
  };

  const getProgressText = (g) => progress[g.id]?.label || `0/${getTargetForGoal(g)}`;
  const getProgressPercent = (g) => progress[g.id]?.percent ?? (g.status===false?100:0);

  const logProgress = async (g, amount = 1) => {
    const { data: { session } } = await supabase.auth.getSession();
    const uid = session?.user?.id;
    if (!uid) return;
    const now = new Date();
    try {
      await supabase.from('goal_progress').insert([{ goalid: g.id, userid: uid, createdat: now, amount }]);
    } catch (e) {
      // fallback to local cache if table missing
      const key = `goal_progress_cache_${uid}`;
      const cache = JSON.parse(localStorage.getItem(key) || '[]');
      cache.push({ goalid: g.id, createdat: now, amount });
      localStorage.setItem(key, JSON.stringify(cache));
    }
    // refresh progress for this goal
    setProgress((prev)=>{
      const nextCount = ((prev[g.id]?.count)||0)+amount;
      const target = (prev[g.id]?.target)||getTargetForGoal(g);
      const percent = Math.max(0, Math.min(100, Math.round((nextCount/target)*100)));
      const suffix = getLabelSuffix(g.frequency);
      const label = `${nextCount}/${target}${suffix ? suffix : ''}`;
      return { ...prev, [g.id]: { ...(prev[g.id]||{}), count: nextCount, target, percent, label } };
    });
  };

  return (
    <div className="goal-tracker">
      <div className="gt-card">
        <div className="gt-header">
          <h3 className="gt-title">Your Standards</h3>
          <p className="gt-subtitle">Track your progress towards your fitness goals</p>
        </div>

        {goals.length > 0 ? (
          <div className="gt-list">
            {goals.map((goal) => (
              <div key={goal.id} className="gt-item" onClick={() => editGoal(goal)}>
                <div className="gt-row">
                  <span className="gt-item-title">{goal.title}</span>
                  <div className="gt-row-actions">
                    <span className="gt-item-meta">{getProgressText(goal)}</span>
                    <button
                      type="button"
                      className="gt-log-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        logProgress(goal);
                      }}
                    >
                      + Log
                    </button>
                    <CheckIcon className='gt-check' onClick={(e) => { e.stopPropagation(); markGoalAsCompleted(goal.id); }} />
                  </div>
                </div>
                <div className="gt-progress">
                  <div className="gt-progress-fill" style={{ width: `${getProgressPercent(goal)}%` }} onClick={(e)=>{ e.stopPropagation(); logProgress(goal); }} title="Click to log progress" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="gt-empty">No active goals yet</div>
        )}

        <button className="gt-add" onClick={toggleModal}>
          <span className="gt-add-icon">◎</span>
          Add New Goal
        </button>
      </div>

      <GoalModal 
        isOpen={isModalOpen} 
        closeModal={toggleModal} 
        addGoal={addGoal} 
        updateGoal={updateGoal} 
        selectedGoal={selectedGoal} // Pass selectedGoal for editing
        markComplete={markGoalAsCompleted} // Pass markGoalAsCompleted to the modal
      />
    </div>
  );
};

// Old accordion UI replaced by card-style list

export default GoalTracker;
